import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useDataApi, type FlexStationParameters } from "@core/apis";
import { useSubstationAddress } from "@core/apis/shared/hooks";
import {
  getFlexStationParametersOptions,
  getStationAdapterConfigOptions,
} from "@core/apis/shared/queries/resource.queries";

const STEP_AMOUNT = 0.5;
const MIN_TEMP = 16;
const MAX_TEMP = 28;

/**
 * Manages the target temperature editor UI (SettingController).
 *
 * @param substationId - Substation identifier
 * @param options.flexStationUid - FlexStation UID for adapter config + parameters.
 *   PropAI: from useFlexStationMetaData(). Platform: from station.subUid prop.
 * @param options.networkUid - Platform requires this for queries and mutations.
 * @param options.address - Substation address for display. Falls back to address lookup.
 * @param options.isAdmin - Admin rights. Platform defaults to true.
 * @param options.isFromPlatform - Displays quick options buttons group depending on which app
 */
export function useTargetTemperatureEditor(
  substationId: string,
  options?: {
    flexStationUid?: string;
    networkUid?: string;
    address?: string;
    isAdmin?: boolean;
  }
) {
  const dataApi = useDataApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["propai_properties"]);
  const [isLoading, setIsLoading] = useState(true);

  const { address } = useSubstationAddress(substationId, options?.address, options?.networkUid);

  // Fetch adapter config for heat optimization status
  const {
    data: adapterConfig,
    isLoading: isAdapterConfigLoading,
    isError: isFlexstationError,
  } = useQuery({
    ...getStationAdapterConfigOptions(
      dataApi,
      options?.flexStationUid,
      substationId,
      options?.networkUid
    ),
    enabled: !!dataApi && !!options?.flexStationUid,
  });

  const heatOptimization = adapterConfig?.allow_local_control ?? false;

  // Fetch flex station parameters for target temperature
  const {
    data: flexstationParameters,
    isLoading: isFlexstationParametersLoading,
    isSuccess: isFlexstationParametersSuccess,
  } = useQuery({
    ...getFlexStationParametersOptions(
      dataApi,
      options?.flexStationUid ?? null,
      substationId,
      options?.networkUid
    ),
    enabled: !!options?.flexStationUid && !!dataApi,
  });

  useEffect(() => {
    if (isAdapterConfigLoading || isFlexstationParametersLoading) return;
    setIsLoading(false);
  }, [isAdapterConfigLoading, isFlexstationParametersLoading]);

  const updateTargetTemperature = useMutation({
    mutationFn: async ({
      flexStationUid,
      body,
    }: {
      flexStationUid: string;
      body: FlexStationParameters;
    }) => {
      return dataApi?.updateFlexStationParameters(
        flexStationUid,
        body,
        substationId,
        options?.networkUid
      );
    },
    onSuccess: async (_data, variables) => {
      if (variables?.flexStationUid) {
        // Invalidate using the shared query key structure
        await queryClient.invalidateQueries({
          queryKey: [
            "network",
            options?.networkUid,
            "substations",
            substationId,
            "flex-stations",
            variables.flexStationUid,
            "parameters",
          ],
        });
        const successMessage = t("text_target_temperature_updated");
        if (successMessage) toast.success(successMessage);
      }
    },
    onError: () => {
      const errorMessage = t("text_target_temperature_update_failed");
      if (errorMessage) toast.error(errorMessage);
    },
  });

  const minTemp = useMemo(() => {
    if (isFlexstationParametersLoading || !flexstationParameters || !isFlexstationParametersSuccess)
      return 0;
    return flexstationParameters.indoor_temp?.min ?? 0;
  }, [flexstationParameters, isFlexstationParametersLoading, isFlexstationParametersSuccess]);

  const [targetTemp, setTargetTemp] = useState<number>(minTemp);
  const [isInputDirty, setIsInputDirty] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    setTargetTemp(typeof minTemp === "number" && Number.isFinite(minTemp) ? minTemp : 0);
  }, [minTemp]);

  const handleNumberKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        setTargetTemp((prev) => {
          const current = typeof prev === "number" && Number.isFinite(prev) ? prev : 0;
          const nextRaw = e.key === "ArrowUp" ? current + STEP_AMOUNT : current - STEP_AMOUNT;
          let next = Math.round(nextRaw * 100) / 100;
          if (next < MIN_TEMP) next = MIN_TEMP;
          if (next > MAX_TEMP) next = MAX_TEMP;
          setIsInputDirty(next !== minTemp);
          return next;
        });
        return;
      }
      e.preventDefault();
    },
    [minTemp]
  );

  const handleNumberPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  }, []);

  const handleNumberWheel = useCallback((e: React.WheelEvent<HTMLInputElement>) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).blur();
  }, []);

  const handleResetTargetTemp = useCallback(() => {
    setTargetTemp(minTemp);
    setIsInputDirty(false);
  }, [minTemp]);

  const handleClosePopover = () => setPopoverAnchorEl(null);

  const handleConfirmTargetTemp = () => {
    if (!options?.flexStationUid) return;

    const body: FlexStationParameters = {
      flexstation_uid: options.flexStationUid,
      indoor_temp: { min: targetTemp },
    };

    updateTargetTemperature.mutate({ flexStationUid: options.flexStationUid, body });
    setIsInputDirty(false);
    handleClosePopover();
  };

  const handleTargetTempChange = (value: number) => {
    setTargetTemp(value);
    setIsInputDirty(value !== minTemp);
  };

  return {
    // State
    isLoading,
    isFlexstationError,
    isFlexstationParametersLoading,
    heatOptimization,
    isAdmin: options?.isAdmin ?? false,
    address: address || "",
    minTemp,
    targetTemp,
    isInputDirty,
    popoverAnchorEl,

    // Input config
    stepAmount: STEP_AMOUNT,
    minTempLimit: MIN_TEMP,
    maxTempLimit: MAX_TEMP,

    // Handlers
    setPopoverAnchorEl,
    handleTargetTempChange,
    handleNumberKeyDown,
    handleNumberPaste,
    handleNumberWheel,
    handleResetTargetTemp,
    handleClosePopover,
    handleConfirmTargetTemp,
  };
}
